import { useEffect, useState } from 'react'
import { api } from '../../api'
import { getCached, setCached } from '../../lib/swr'
import { ORANGE, VERT, VERT_OK, eur, estCanalMarche, estStandDefaut, netMarche, offreEmoji, offreNom } from './marcheProducer'

// Meme cle que l'onglet Événements : les deux lisent les offres au canal Marche.
const CLE_OFFRES = 'producer/offers'

/**
 * Marche producteur — onglet « Mon stand » (proto v7.2.1, mStandP).
 * Vitrine de REFERENCE : offres au canal Marche actif, chacune activable/
 * desactivable (standDefaut persiste — c'est le modele pre-coche a chaque
 * inscription). L'ajout d'un produit = simple lien vers Mes offres (§5 v7.1 :
 * jamais de second chemin de creation).
 */
function MarcheProducerStand({ onGoOffers, onToast }) {
  // Cache SWR (S29, E-13) : le stand deja vu s'affiche sans squelette.
  const [offres, setOffres] = useState(() => getCached(CLE_OFFRES) || [])
  const [loading, setLoading] = useState(() => getCached(CLE_OFFRES) === undefined)
  const [pending, setPending] = useState(null) // offerId en cours de bascule

  useEffect(() => {
    api.producer.getOffers()
      .then((all) => {
        const marche = (all || []).filter((o) => o.status === 'ACTIVE' && estCanalMarche(o))
        setOffres(marche)
        setCached(CLE_OFFRES, marche)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const basculer = async (offre) => {
    const prochain = !estStandDefaut(offre)
    setPending(offre.id)
    try {
      await api.producer.setStandDefaut(offre.id, prochain)
      // Met a jour le canal MARCHE localement (meme forme que le backend), et
      // dans le cache : l'onglet Événements pre-coche son stand a partir de la.
      setOffres((os) => {
        const maj = os.map((o) => {
          if (o.id !== offre.id) return o
          const channels = [...(o.channels || [])]
          const i = channels.findIndex((c) => c?.module?.code === 'MARCHE')
          if (i >= 0) channels[i] = { ...channels[i], standDefaut: prochain }
          else channels.push({ module: { code: 'MARCHE' }, isActive: true, standDefaut: prochain })
          return { ...o, channels }
        })
        setCached(CLE_OFFRES, maj)
        return maj
      })
    } catch (e) {
      onToast?.(e.message || 'La mise à jour a échoué.')
    } finally {
      setPending(null)
    }
  }

  if (loading) {
    return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />
  }

  return (
    <>
      {/* Hero glass fonce (proto) */}
      <div className="glass-dark rounded-[22px] mb-4 flex items-center gap-3 relative overflow-hidden" style={{ padding: '16px 18px', color: 'rgb(var(--c-creme))' }}>
        <div
          className="absolute -top-8 -right-5 w-[120px] h-[120px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(244,137,83,0.4), transparent 70%)' }}
        />
        <span className="text-[34px] relative">🪧</span>
        <div className="relative">
          <p className="font-serif text-[19px]">Mes produits par défaut</p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(247,240,234,0.75)' }}>Ce que vous apportez d'habitude aux évènements.</p>
        </div>
      </div>
      <p className="text-[11.5px] leading-relaxed mb-3 mx-0.5 text-ink/55">
        🦊 Ces produits viennent de vos offres au canal Évènements. À chaque inscription, cette liste vous sera
        proposée pré-remplie — vous n'aurez qu'à décocher ce que vous n'apportez pas.
      </p>

      {offres.length ? (
        <div className="flex flex-col gap-2.5">
          {offres.map((o) => {
            const dans = estStandDefaut(o)
            return (
              <button
                key={o.id}
                onClick={() => basculer(o)}
                disabled={pending === o.id}
                className="glass-surface rounded-[18px] flex items-center gap-3 text-left"
                style={{ padding: '13px 15px', opacity: dans ? 1 : 0.5 }}
              >
                <span className="text-[26px] flex-shrink-0">{offreEmoji(o)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold" style={{ color: VERT }}>{offreNom(o)}</p>
                  <p className="text-[11.5px] text-ink/55">
                    Vous recevez <span className="font-serif font-bold" style={{ color: VERT }}>{eur(netMarche(o))}</span>
                    {o.prixUnit ? ` / ${o.prixUnit}` : ''}
                  </p>
                </div>
                <span
                  className="rounded-full relative flex-shrink-0"
                  style={{ width: 46, height: 27, background: dans ? VERT_OK : 'rgba(7,62,61,0.2)', transition: 'background .2s' }}
                >
                  <span
                    className="absolute rounded-full bg-white"
                    style={{ top: 3, left: dans ? 22 : 3, width: 21, height: 21, transition: 'left .2s' }}
                  />
                </span>
              </button>
            )
          })}
          <button
            onClick={onGoOffers}
            className="glass-surface rounded-[18px] text-[13px] font-bold flex items-center justify-center gap-1.5"
            style={{ padding: '13px 15px', color: VERT }}
          >
            + Ajouter un produit <span className="text-[11px] font-semibold text-ink/50">· dans Mes offres ›</span>
          </button>
        </div>
      ) : (
        <div className="glass-surface rounded-[20px] text-center" style={{ padding: '20px 16px' }}>
          <div className="text-4xl mb-2">🪧</div>
          <p className="text-[13px] leading-relaxed text-ink/65">
            Aucun produit au canal Évènements. Activez le canal <strong>Évènements</strong> sur vos offres pour qu'ils apparaissent ici.
          </p>
          <button
            onClick={onGoOffers}
            className="mt-3 rounded-full text-[13px] font-extrabold"
            style={{ padding: '12px 20px', border: 'none', background: ORANGE, color: VERT }}
          >
            Gérer mes offres ›
          </button>
        </div>
      )}
    </>
  )
}

export default MarcheProducerStand
